import type * as React from "react";
import { Input as InputPrimitive } from "@base-ui/react/input";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@corpuscomplete/web-ui/lib/utils";
import { textVariants } from "@corpuscomplete/web-ui/components/text";

const inputVariants = cva(
  "w-full min-w-0 outline-none transition-colors disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50",
  {
    variants: {
      variant: {
        default:
          "bg-input/20 dark:bg-input/30 border-input focus-visible:border-ring focus-visible:ring-ring/30 aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive dark:aria-invalid:border-destructive/50 h-7 rounded-md border px-2 py-0.5 text-sm transition-colors file:h-6 file:text-xs/relaxed file:font-medium focus-visible:ring-[2px] aria-invalid:ring-[2px] md:text-xs/relaxed file:text-foreground placeholder:text-muted-foreground file:inline-flex file:border-0 file:bg-transparent",
        ghost:
          "border-0 bg-transparent px-0 py-0 shadow-none focus:border-0 focus:shadow-none focus:outline-none focus:ring-0 focus-visible:outline-none focus-visible:ring-0 placeholder:text-muted-foreground",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  },
);

type TextVariant = VariantProps<typeof textVariants>["variant"];

interface InputProps extends React.ComponentProps<"input">, VariantProps<typeof inputVariants> {
  /**
   * Apply text styling from the Text component's variants.
   * Useful with the "ghost" variant to make the input appear as a heading or other text element.
   */
  textVariant?: TextVariant;
}

function Input({ className, type, variant, textVariant, ...props }: InputProps) {
  return (
    <InputPrimitive
      type={type}
      data-slot="input"
      className={cn(
        inputVariants({ variant }),
        textVariant && textVariants({ variant: textVariant }),
        className,
      )}
      {...props}
    />
  );
}

export { Input, inputVariants, type InputProps };
